/**
 * @lastAudited 2025-01-19
 * @auditStatus PASS
 * @auditNotes Rebuilt as cinematic trailer-style VDP with dark theme
 */
import { useState, useEffect, useRef, useCallback, Suspense, memo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useVehicleById } from '../../hooks/useVehicleById';
import { type Vehicle } from '../../lib/vehicles';
import { useVehicleDetailSchemas } from '../../hooks/useVehicleDetailSchemas';
import { useVideoSchema } from '../../hooks/useVideoSchema';
import { useSEOHead } from '../../hooks/useSEOHead';
import { useReviews } from '../../hooks/useReviews';
import { useRecentlyViewed } from '../../hooks/useRecentlyViewed';
import ErrorBoundary, { retryLazy } from '../../components/base/ErrorBoundary';

import VehicleDetailSkeleton from './components/VehicleDetailSkeleton';
import BackToTop from '../../components/feature/BackToTop';
import StickySidebar from './components/StickySidebar';
import HowYourPurchaseHelps from './components/HowYourPurchaseHelps';
import StickyActionBar from './components/StickyActionBar';

const SimilarVehicles        = retryLazy(() => import('./components/SimilarVehicles'));
const CheckAvailabilityModal = retryLazy(() => import('./components/CheckAvailabilityModal'));
const PreApprovalForm        = retryLazy(() => import('./components/PreApprovalForm'));

function SectionFallback() {
  return (
    <div className="bg-[#F7F5F0] rounded-2xl border border-[#E8E2D6] px-5 py-6 animate-pulse">
      <div className="h-4 bg-[#E8E2D6] rounded w-1/3 mb-4" />
      <div className="space-y-2">
        <div className="h-3 bg-[#E8E2D6] rounded w-full" />
        <div className="h-3 bg-[#E8E2D6] rounded w-5/6" />
      </div>
    </div>
  );
}

function extractYouTubeId(url: string): string | null {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/]+)/,
    /^([a-zA-Z0-9_-]{11})$/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

// ─── Cinematic Hero ───────────────────────────────────────────────────────────
const CinematicHero = memo(({ vehicle, onWatchTrailer }: { vehicle: Vehicle; onWatchTrailer: () => void }) => {
  const hasVideo = !!vehicle.video_url;

  return (
    <div
      className="relative w-full overflow-hidden"
      style={{ minHeight: '560px', height: '100dvh' }}
      role="banner"
    >
      {/* Background Image */}
      <div className="absolute inset-0" aria-hidden="true">
        <img
          src={vehicle.image || ''}
          alt=""
          className="w-full h-full object-cover"
          style={{ objectPosition: 'center 30%' }}
          fetchPriority="high"
          decoding="sync"
          loading="eager"
          width={1600}
          height={900}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to top, #1E1A14 0%, rgba(20,16,10,0.92) 45%, rgba(20,16,10,0.55) 75%, rgba(20,16,10,0.35) 100%), linear-gradient(to right, rgba(20,16,10,0.95) 0%, rgba(20,16,10,0.55) 55%, transparent 100%)',
          }}
        />
      </div>

      {/* Top bar — desktop only */}
      <div className="absolute left-0 right-0 hidden sm:flex items-center gap-3 px-8" style={{ top: 'calc(var(--nav-height, 72px) + 16px)' }} aria-hidden="true">
        <div className="flex gap-1">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="w-4 h-3 rounded-sm bg-white/10 border border-white/5" />
          ))}
        </div>
        <span className="text-white/50 text-xs font-mono tracking-widest">ROCKHOUND AUTO PRESENTS</span>
        <div className="flex gap-1 ml-auto">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="w-4 h-3 rounded-sm bg-white/10 border border-white/5" />
          ))}
        </div>
      </div>

      {/* Main content */}
      <div
        className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-16 flex flex-col justify-end pb-10 sm:pb-14"
        style={{ paddingTop: 'calc(var(--nav-height, 72px) + 48px)' }}
      >
        {/* Badges */}
        <div className="flex items-center gap-2 mb-3 sm:mb-4 flex-wrap" aria-label="Vehicle status">
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded text-xs font-black uppercase tracking-widest"
            style={{ background: '#F5C842', color: '#1A1208', fontFamily: "'Oswald', sans-serif", letterSpacing: '0.14em' }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#1A1208] animate-pulse" aria-hidden="true" />
            NOW PLAYING
          </span>
          {vehicle.status && vehicle.status !== 'available' && (
            <span className="px-3 py-1 rounded text-xs font-bold uppercase bg-red-600 text-white tracking-wider">
              {vehicle.status}
            </span>
          )}
          {vehicle.bodyType && (
            <span className="px-3 py-1 rounded text-xs font-semibold text-white border border-white/40 tracking-wide bg-black/30">
              {vehicle.bodyType}
            </span>
          )}
          {vehicle.year && (
            <span className="px-3 py-1 rounded text-xs font-semibold text-white border border-white/40 tracking-wide bg-black/30">
              {vehicle.year}
            </span>
          )}
        </div>

        {/* Title treatment */}
        <div className="mb-3 sm:mb-4">
          <h1
            className="text-white font-black leading-none mb-1.5 sm:mb-2 drop-shadow-2xl"
            style={{ fontFamily: "'Oswald', sans-serif", fontSize: 'clamp(1.75rem, 7vw, 5.5rem)', letterSpacing: '0.01em', textShadow: '0 2px 12px rgba(0,0,0,0.7)' }}
          >
            {vehicle.make} <span style={{ color: '#F5C842' }}>{vehicle.model}</span>
          </h1>
          {vehicle.trim && (
            <p className="text-white/90 text-sm sm:text-xl font-light tracking-[0.2em] uppercase mb-2 sm:mb-3" style={{ textShadow: '0 1px 6px rgba(0,0,0,0.6)' }}>
              {vehicle.trim} Edition
            </p>
          )}
        </div>

        {/* Inline specs */}
        <ul className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mb-4 sm:mb-5 text-sm list-none p-0 m-0" aria-label="Key specifications">
          {[
            { icon: 'ri-road-map-line', val: `${vehicle.mileage.toLocaleString()} mi`, label: 'Mileage' },
            { icon: 'ri-settings-3-line', val: vehicle.transmission, label: 'Transmission' },
            { icon: 'ri-steering-2-line', val: vehicle.drivetrain, label: 'Drivetrain' },
            { icon: 'ri-gas-station-line', val: vehicle.fuelType || 'Gasoline', label: 'Fuel type' },
          ].filter(s => s.val).map((s, i) => (
            <li key={i} className={`flex items-center gap-1.5 text-white font-medium ${i >= 2 ? 'hidden sm:flex' : ''}`} style={{ textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>
              <i className={`${s.icon}`} style={{ color: '#F5C842' }} aria-hidden="true" />
              <span className="sr-only">{s.label}: </span>
              {s.val}
            </li>
          ))}
        </ul>

        {/* Price */}
        <div className="flex items-baseline gap-3 mb-5 sm:mb-6 flex-wrap">
          <span
            className="text-white font-black drop-shadow-2xl"
            style={{ fontFamily: "'Oswald', sans-serif", fontSize: 'clamp(1.75rem, 5vw, 4rem)', textShadow: '0 2px 12px rgba(0,0,0,0.7)' }}
            aria-label={`Price: $${vehicle.price.toLocaleString()}`}
          >
            ${vehicle.price.toLocaleString()}
          </span>
          <span className="text-white/80 text-sm sm:text-base font-medium" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>
            or ~${Math.round((() => {
              const loan = Math.max(vehicle.price - Math.round(vehicle.price * 0.1), 0);
              const r = 5.9 / 100 / 12;
              const n = 60;
              return (loan * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
            })()).toLocaleString()}/mo
          </span>
        </div>

        {/* CTA row */}
        <div className="flex flex-wrap gap-2 sm:gap-3" role="group" aria-label="Vehicle actions">
          {hasVideo && (
            <button
              onClick={onWatchTrailer}
              className="inline-flex items-center gap-2 sm:gap-3 px-4 sm:px-7 py-2.5 sm:py-3.5 font-black text-sm sm:text-base rounded-xl transition-all hover:scale-105 cursor-pointer whitespace-nowrap shadow-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black/50"
              style={{ background: 'white', color: '#1E1A14', fontFamily: "'Oswald', sans-serif", letterSpacing: '0.08em' }}
              aria-label="Watch video trailer for this vehicle"
            >
              <i className="ri-play-fill text-base sm:text-xl" aria-hidden="true" />
              WATCH TRAILER
            </button>
          )}
          <button
            onClick={() => document.getElementById('showtimes')?.scrollIntoView({ behavior: 'smooth' })}
            className="inline-flex items-center gap-2 sm:gap-3 px-4 sm:px-7 py-2.5 sm:py-3.5 font-black text-sm sm:text-base rounded-xl transition-all hover:scale-105 cursor-pointer whitespace-nowrap shadow-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F5C842] focus-visible:ring-offset-2 focus-visible:ring-offset-black/50"
            style={{ background: '#F5C842', color: '#1A1208', fontFamily: "'Oswald', sans-serif", letterSpacing: '0.08em' }}
            aria-label="Jump to claim this ride section"
          >
            <i className="ri-film-line text-base sm:text-xl" aria-hidden="true" />
            SEE DETAILS
          </button>
          <button
            onClick={() => document.getElementById('gallery-section')?.scrollIntoView({ behavior: 'smooth' })}
            className="inline-flex items-center gap-2 sm:gap-3 px-4 sm:px-7 py-2.5 sm:py-3.5 font-black text-sm sm:text-base rounded-xl transition-all hover:scale-105 cursor-pointer whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black/50"
            style={{ background: 'rgba(0,0,0,0.45)', color: 'white', border: '1.5px solid rgba(255,255,255,0.55)', fontFamily: "'Oswald', sans-serif", letterSpacing: '0.08em', backdropFilter: 'blur(8px)' }}
            aria-label="Jump to photo gallery section"
          >
            <i className="ri-gallery-line text-base sm:text-xl" aria-hidden="true" />
            GALLERY
          </button>
        </div>
      </div>

      {/* Bottom film strip — desktop only */}
      <div className="absolute bottom-0 left-0 right-0 hidden sm:flex items-center gap-1 px-8 pb-3 opacity-20" aria-hidden="true">
        {[...Array(40)].map((_, i) => (
          <div key={i} className="flex-1 h-2 rounded-sm bg-white/30 border border-white/10" />
        ))}
      </div>
    </div>
  );
});
CinematicHero.displayName = 'CinematicHero';

// ─── Video Modal ──────────────────────────────────────────────────────────────
const VideoModal = memo(({ videoUrl, vehicleName, onClose }: { videoUrl: string; vehicleName: string; onClose: () => void }) => {
  const youtubeId = extractYouTubeId(videoUrl);
  const closeRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    previousFocusRef.current = document.activeElement as HTMLElement;
    requestAnimationFrame(() => closeRef.current?.focus());
    return () => {
      document.body.style.overflow = '';
      previousFocusRef.current?.focus();
    };
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/97 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`${vehicleName} — video trailer`}
      onClick={onClose}
    >
      <button
        ref={closeRef}
        onClick={onClose}
        className="absolute top-5 right-5 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white text-2xl cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
        aria-label="Close video"
      >
        <i className="ri-close-line" aria-hidden="true" />
      </button>
      <div className="absolute top-5 left-1/2 -translate-x-1/2 text-white/50 text-sm font-mono tracking-widest" aria-hidden="true">
        {vehicleName} — Official Trailer
      </div>
      <div className="w-full max-w-6xl aspect-video" onClick={(e) => e.stopPropagation()}>
        <iframe
          src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1`}
          title={`${vehicleName} — Trailer`}
          className="w-full h-full rounded-xl"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    </div>
  );
});
VideoModal.displayName = 'VideoModal';

// ─── Synopsis ─────────────────────────────────────────────────────────────────
const Synopsis = memo(({ vehicle }: { vehicle: Vehicle }) => {
  const [synopsis, setSynopsis] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState<string>('');

  useEffect(() => {
    if (!vehicle) return;
    let cancelled = false;
    setLoading(true);
    setSynopsis(null);

    const fetchSynopsis = async () => {
      try {
        const res = await fetch(
          'https://yshatcivfpnzxcgppout.supabase.co/functions/v1/generate-vehicle-synopsis',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ vehicle }),
          }
        );
        if (!res.ok) throw new Error('fetch failed');
        const data = await res.json();
        if (!cancelled) {
          setSynopsis(data.synopsis || vehicle.description || '');
          setSource(data.source || '');
        }
      } catch {
        if (!cancelled) {
          setSynopsis(vehicle.description || '');
          setSource('fallback');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchSynopsis();
    return () => { cancelled = true; };
  }, [vehicle.id, vehicle.uuid]);

  return (
    <section className="bg-[#FFFBF0] rounded-2xl border border-[#E8E2D6] p-7 sm:p-9" aria-labelledby="synopsis-heading">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-1 h-8 rounded-full bg-[#F5C842]" aria-hidden="true" />
        <h2 id="synopsis-heading" className="text-2xl font-black text-[#1A2332]" style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: '0.06em' }}>
          SYNOPSIS
        </h2>
        <div className="flex-1 h-px bg-[#E8E2D6] ml-2" aria-hidden="true" />
        {!loading && (source === 'openai') && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[#F5C842]/20 text-[#D97706] border border-[#F5C842]/40 whitespace-nowrap">
            <i className="ri-sparkling-2-line text-xs" aria-hidden="true" />
            AI Generated
          </span>
        )}
        {!loading && (source === 'built-in' || source === 'built-in-fallback') && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[#E8E2D6]/60 text-[#6B7A8D] border border-[#E8E2D6] whitespace-nowrap">
            <i className="ri-robot-line text-xs" aria-hidden="true" />
            Smart Summary
          </span>
        )}
      </div>

      {loading ? (
        <div className="space-y-3 animate-pulse" aria-label="Loading synopsis…" role="status">
          <div className="h-4 bg-[#E8E2D6] rounded-full w-full" />
          <div className="h-4 bg-[#E8E2D6] rounded-full w-11/12" />
          <div className="h-4 bg-[#E8E2D6] rounded-full w-4/5" />
          <div className="h-4 bg-[#E8E2D6] rounded-full w-full mt-4" />
          <div className="h-4 bg-[#E8E2D6] rounded-full w-10/12" />
          <div className="h-4 bg-[#E8E2D6] rounded-full w-3/4" />
          <div className="h-4 bg-[#E8E2D6] rounded-full w-full mt-4" />
          <div className="h-4 bg-[#E8E2D6] rounded-full w-9/12" />
          <span className="sr-only">Generating synopsis…</span>
        </div>
      ) : (
        <p
          className="text-base text-[#4A5568] leading-relaxed whitespace-pre-line"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {synopsis}
        </p>
      )}
    </section>
  );
});
Synopsis.displayName = 'Synopsis';

// ─── Movie Poster Specs ───────────────────────────────────────────────────────
const MoviePosterSpecs = memo(({ vehicle }: { vehicle: Vehicle }) => {
  const specs = [
    { label: 'YEAR', value: String(vehicle.year), icon: 'ri-calendar-line' },
    { label: 'MAKE', value: vehicle.make, icon: 'ri-car-line' },
    { label: 'MODEL', value: vehicle.model, icon: 'ri-car-line' },
    { label: 'TRIM', value: vehicle.trim || '—', icon: 'ri-star-line' },
    { label: 'MILEAGE', value: `${vehicle.mileage.toLocaleString()} mi`, icon: 'ri-road-map-line' },
    { label: 'BODY', value: vehicle.bodyType || '—', icon: 'ri-car-washing-line' },
    { label: 'FUEL', value: vehicle.fuelType || '—', icon: 'ri-gas-station-line' },
    { label: 'TRANS.', value: vehicle.transmission || '—', icon: 'ri-settings-3-line' },
    { label: 'DRIVE', value: vehicle.drivetrain || '—', icon: 'ri-steering-2-line' },
    { label: 'ENGINE', value: vehicle.engineSize || '—', icon: 'ri-settings-2-line' },
    { label: 'EXT. COLOR', value: vehicle.exteriorColor || '—', icon: 'ri-palette-line' },
    { label: 'INT. COLOR', value: vehicle.interiorColor || '—', icon: 'ri-palette-2-line' },
  ];

  return (
    <section className="bg-[#FFFBF0] rounded-2xl border border-[#E8E2D6] p-5 sm:p-7 lg:p-9" aria-labelledby="specs-heading">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-1 h-8 rounded-full bg-[#F5C842]" aria-hidden="true" />
        <h2 id="specs-heading" className="text-2xl font-black text-[#1A2332]" style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: '0.06em' }}>
          CAST &amp; SPECS
        </h2>
        <div className="flex-1 h-px bg-[#E8E2D6] ml-2" aria-hidden="true" />
      </div>

      <dl className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
        {specs.map((spec, i) => (
          <div
            key={i}
            className="relative rounded-xl p-3 sm:p-4 overflow-hidden group"
            style={{ background: '#F7F5F0', border: '1px solid #E8E2D6' }}
          >
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#F5C842] opacity-0 group-hover:opacity-100 transition-opacity rounded-t-xl" aria-hidden="true" />
            <div className="flex items-center gap-1.5 mb-1.5">
              <i className={`${spec.icon} text-xs`} style={{ color: '#D97706' }} aria-hidden="true" />
              <dt className="text-[9px] sm:text-[10px] text-[#6B7A8D] font-black uppercase tracking-widest truncate">{spec.label}</dt>
            </div>
            <dd className="text-xs sm:text-sm font-bold text-[#1A2332] break-words leading-snug">{spec.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
});
MoviePosterSpecs.displayName = 'MoviePosterSpecs';

// ─── Scene Gallery ────────────────────────────────────────────────────────────
const SceneGallery = memo(({ images, vehicleName }: { images: string[]; vehicleName: string }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const closeLightboxRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const ITEM_WIDTH = 300;
  const ITEM_GAP = 12;

  // Batch geometric reads with requestAnimationFrame to prevent forced reflow
  const scrollToIndex = useCallback((idx: number) => {
    const container = scrollRef.current;
    const item = itemRefs.current[idx];
    if (!container || !item) return;

    requestAnimationFrame(() => {
      if (!container || !item) return;
      const containerWidth = container.offsetWidth;
      const itemLeft = idx * (ITEM_WIDTH + ITEM_GAP);
      const scrollTarget = itemLeft - (containerWidth / 2) + (ITEM_WIDTH / 2);
      container.scrollTo({ left: Math.max(0, scrollTarget), behavior: 'smooth' });
    });
  }, []);

  const goTo = useCallback((idx: number) => {
    const clamped = Math.max(0, Math.min(images.length - 1, idx));
    setActiveIdx(clamped);
    scrollToIndex(clamped);
  }, [images.length, scrollToIndex]);

  const openLightbox = useCallback((idx: number, trigger: HTMLElement) => {
    previousFocusRef.current = trigger;
    setLightboxIdx(idx);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxIdx(null);
    requestAnimationFrame(() => previousFocusRef.current?.focus());
  }, []);

  // Focus close button when lightbox opens
  useEffect(() => {
    if (lightboxIdx !== null) {
      requestAnimationFrame(() => closeLightboxRef.current?.focus());
    }
  }, [lightboxIdx]);

  useEffect(() => {
    if (lightboxIdx === null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') setLightboxIdx(p => p !== null ? (p + 1) % images.length : null);
      if (e.key === 'ArrowLeft') setLightboxIdx(p => p !== null ? (p - 1 + images.length) % images.length : null);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [lightboxIdx, images.length, closeLightbox]);

  return (
    <>
      <section id="gallery-section" className="bg-[#FFFBF0] rounded-2xl border border-[#E8E2D6] p-7 sm:p-9" aria-labelledby="gallery-heading">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-1 h-8 rounded-full bg-[#F5C842]" aria-hidden="true" />
          <h2 id="gallery-heading" className="text-2xl font-black text-[#1A2332]" style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: '0.06em' }}>
            SCENE GALLERY
          </h2>
          <span className="text-[#6B7A8D] text-xs ml-1" aria-label={`${images.length} photos`}>{images.length} photos</span>
          <div className="flex-1 h-px bg-[#E8E2D6] ml-2" aria-hidden="true" />
        </div>

        {/* Scroll area with arrow buttons */}
        <div className="flex items-center gap-3" role="group" aria-label="Photo gallery navigation">
          <button
            onClick={() => goTo(activeIdx - 1)}
            disabled={activeIdx === 0}
            className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-[#F5C842] hover:bg-[#1A2332] text-[#1E1A14] hover:text-white rounded-full cursor-pointer transition-colors shadow-md disabled:opacity-30 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A2332]"
            aria-label="Previous photo"
          >
            <i className="ri-arrow-left-s-line text-xl" aria-hidden="true" />
          </button>

          <div
            ref={scrollRef}
            className="flex gap-3 overflow-x-auto scroll-smooth flex-1"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', willChange: 'scroll-position' }}
            role="region"
            aria-label="Photo strip"
          >
            {images.map((img, i) => (
              <button
                key={i}
                ref={(el) => { itemRefs.current[i] = el; }}
                onClick={(e) => { setActiveIdx(i); openLightbox(i, e.currentTarget); }}
                className={`flex-shrink-0 rounded-xl overflow-hidden border-2 transition-all cursor-pointer group/img focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F5C842] ${
                  activeIdx === i ? 'border-[#F5C842] ring-2 ring-[#F5C842]/40 scale-[1.03]' : 'border-[#E8E2D6] hover:border-[#F5C842]'
                }`}
                style={{ width: ITEM_WIDTH, height: 200, willChange: activeIdx === i ? 'transform' : 'auto' }}
                aria-label={`View photo ${i + 1} of ${images.length}`}
                aria-pressed={activeIdx === i}
              >
                <img
                  src={img}
                  alt={`${vehicleName} — photo ${i + 1} of ${images.length}`}
                  className="w-full h-full object-cover group-hover/img:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </button>
            ))}
          </div>

          <button
            onClick={() => goTo(activeIdx + 1)}
            disabled={activeIdx === images.length - 1}
            className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-[#F5C842] hover:bg-[#1A2332] text-[#1E1A14] hover:text-white rounded-full cursor-pointer transition-colors shadow-md disabled:opacity-30 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A2332]"
            aria-label="Next photo"
          >
            <i className="ri-arrow-right-s-line text-xl" aria-hidden="true" />
          </button>
        </div>

        {/* Dot indicators */}
        {images.length > 1 && (
          <div className="flex items-center justify-center gap-1.5 mt-4" role="group" aria-label="Photo position indicators">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`rounded-full transition-all cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F5C842] ${
                  activeIdx === i ? 'w-5 h-2 bg-[#F5C842]' : 'w-2 h-2 bg-[#D1C9B8] hover:bg-[#F5C842]/60'
                }`}
                style={activeIdx === i ? { willChange: 'width' } : undefined}
                aria-label={`Go to photo ${i + 1}`}
                aria-current={activeIdx === i ? 'true' : undefined}
              />
            ))}
          </div>
        )}
      </section>

      {/* Lightbox */}
      {lightboxIdx !== null && (
        <div
          className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label={`Photo ${lightboxIdx + 1} of ${images.length} — ${vehicleName}`}
          onClick={closeLightbox}
        >
          <button
            ref={closeLightboxRef}
            onClick={closeLightbox}
            className="absolute top-5 right-5 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white text-2xl cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            aria-label="Close photo viewer"
          >
            <i className="ri-close-line" aria-hidden="true" />
          </button>
          <div className="absolute top-5 left-1/2 -translate-x-1/2 text-white/40 text-sm font-mono" aria-live="polite" aria-atomic="true">
            {lightboxIdx + 1} / {images.length}
          </div>
          <img
            src={images[lightboxIdx]}
            alt={`${vehicleName} — enlarged photo ${lightboxIdx + 1} of ${images.length}`}
            className="max-w-5xl max-h-[80vh] object-contain rounded-xl"
            onClick={e => e.stopPropagation()}
          />
          {images.length > 1 && (
            <>
              <button
                onClick={e => { e.stopPropagation(); setLightboxIdx(p => p !== null ? (p - 1 + images.length) % images.length : 0); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                aria-label="Previous photo"
              >
                <i className="ri-arrow-left-s-line text-2xl" aria-hidden="true" />
              </button>
              <button
                onClick={e => { e.stopPropagation(); setLightboxIdx(p => p !== null ? (p + 1) % images.length : 0); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                aria-label="Next photo"
              >
                <i className="ri-arrow-right-s-line text-2xl" aria-hidden="true" />
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
});
SceneGallery.displayName = 'SceneGallery';

// ─── Video Walkthrough Section ────────────────────────────────────────────────
const VideoWalkthroughSection = memo(({ vehicle, onPlay }: { vehicle: Vehicle; onPlay: () => void }) => {
  const youtubeId = vehicle.video_url ? extractYouTubeId(vehicle.video_url) : null;
  const [playing, setPlaying] = useState(false);

  if (!youtubeId) return null;

  const thumbUrl = `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`;

  return (
    <section className="rounded-2xl overflow-hidden border border-[#E8E2D6]" aria-labelledby="trailer-heading">
      {/* Section header */}
      <div className="bg-[#1E1A14] px-7 py-5 flex items-center gap-3 border-b border-[#2C2518]">
        <div className="w-1 h-8 rounded-full bg-[#F5C842]" aria-hidden="true" />
        <h2 id="trailer-heading" className="text-2xl font-black text-white" style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: '0.06em' }}>
          OFFICIAL TRAILER
        </h2>
        <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest bg-red-600 text-white ml-1" aria-label="YouTube video">
          YouTube
        </span>
        <div className="flex-1 h-px bg-white/10 ml-2" aria-hidden="true" />
      </div>

      {/* Video player */}
      <div className="relative w-full bg-[#1E1A14]" style={{ aspectRatio: '16/9' }}>
        {playing ? (
          <iframe
            src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1`}
            title={`${vehicle.year} ${vehicle.make} ${vehicle.model} — Official Trailer`}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <>
            <img
              src={thumbUrl}
              alt={`${vehicle.year} ${vehicle.make} ${vehicle.model} trailer thumbnail`}
              className="w-full h-full object-cover"
              onError={(e) => { (e.target as HTMLImageElement).src = `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`; }}
            />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(30,26,20,0.75) 0%, rgba(30,26,20,0.2) 50%, transparent 100%)' }} aria-hidden="true" />
            {/* Film strip top — decorative */}
            <div className="absolute top-0 left-0 right-0 flex gap-1 px-4 pt-3 opacity-25" aria-hidden="true">
              {[...Array(30)].map((_, i) => <div key={i} className="flex-1 h-2 bg-white/40 rounded-sm" />)}
            </div>

            {/* Play button */}
            <button
              onClick={() => setPlaying(true)}
              className="absolute inset-0 flex flex-col items-center justify-center cursor-pointer group/play focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#F5C842] focus-visible:ring-inset"
              aria-label={`Play trailer for ${vehicle.year} ${vehicle.make} ${vehicle.model}`}
            >
              <div
                className="w-24 h-24 flex items-center justify-center rounded-full mb-4 transition-all duration-300 group-hover/play:scale-110 shadow-2xl"
                style={{ background: 'rgba(245,200,66,0.95)', border: '3px solid rgba(255,255,255,0.3)' }}
                aria-hidden="true"
              >
                <i className="ri-play-fill text-5xl ml-2" style={{ color: '#1E1A14' }} />
              </div>
              <span className="text-white font-black text-lg tracking-widest uppercase drop-shadow-xl" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Play Trailer
              </span>
              {vehicle.video_duration && (
                <span className="text-white/60 text-sm mt-1">{vehicle.video_duration}</span>
              )}
            </button>

            {/* Film strip bottom — decorative */}
            <div className="absolute bottom-0 left-0 right-0 flex gap-1 px-4 pb-3 opacity-25" aria-hidden="true">
              {[...Array(30)].map((_, i) => <div key={i} className="flex-1 h-2 bg-white/40 rounded-sm" />)}
            </div>
          </>
        )}
      </div>

      {/* Info bar */}
      <div className="bg-[#1E1A14] px-7 py-4 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-white font-bold text-sm" style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: '0.04em' }}>
            {vehicle.year} {vehicle.make} {vehicle.model} — Full Walkthrough
          </p>
          <p className="text-white/40 text-xs mt-0.5">Presented by Rockhound Auto · Warrenton, Oregon</p>
        </div>
        <a
          href={`https://www.youtube.com/watch?v=${youtubeId}`}
          target="_blank"
          rel="nofollow noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white/70 hover:text-white transition-colors cursor-pointer whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F5C842]"
          style={{ background: 'rgba(245,200,66,0.12)', border: '1px solid rgba(245,200,66,0.25)' }}
          aria-label={`Watch ${vehicle.year} ${vehicle.make} ${vehicle.model} on YouTube (opens in new tab)`}
        >
          <i className="ri-external-link-line" aria-hidden="true" />
          Watch on YouTube
        </a>
      </div>
    </section>
  );
});
VideoWalkthroughSection.displayName = 'VideoWalkthroughSection';

// ─── Critic Score ─────────────────────────────────────────────────────────────
const CriticScore = memo(({ honestyScore }: { honestyScore?: number }) => {
  if (!honestyScore) return null;

  const score = Math.max(1, Math.min(5, honestyScore));
  const pct = Math.round((score / 5) * 100);

  const config =
    score >= 5
      ? { label: '🐾 ROCKHOUND APPROVED', sub: 'Squeaky clean — we know everything about this one.', color: '#22c55e', border: '#22c55e', bg: 'rgba(34,197,94,0.08)' }
      : score >= 4
      ? { label: '🏅 TRAIL-TESTED', sub: 'Strong history, minor mysteries — nothing sketchy.', color: '#16a34a', border: '#16a34a', bg: 'rgba(22,163,74,0.08)' }
      : score >= 3
      ? { label: '🔍 MOSTLY TRANSPARENT', sub: "We're honest about what we don't know.", color: '#D97706', border: '#D97706', bg: 'rgba(217,119,6,0.08)' }
      : score >= 2
      ? { label: '⚠️ SOME UNKNOWNS', sub: "Good car, limited history. We'll tell you why.", color: '#ea580c', border: '#ea580c', bg: 'rgba(234,88,12,0.08)' }
      : { label: '❓ MYSTERY MACHINE', sub: "We know it runs. That's a start.", color: '#dc2626', border: '#dc2626', bg: 'rgba(220,38,38,0.08)' };

  return (
    <section className="bg-[#FFFBF0] rounded-2xl border border-[#E8E2D6] p-5 sm:p-7 lg:p-9" aria-labelledby="honesty-heading">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-1 h-8 rounded-full bg-[#F5C842]" aria-hidden="true" />
        <h2 id="honesty-heading" className="text-2xl font-black text-[#1A2332]" style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: '0.06em' }}>
          HONESTY SCORE
        </h2>
        <div className="flex-1 h-px bg-[#E8E2D6] ml-2" aria-hidden="true" />
      </div>

      {/* Stack vertically on mobile, side-by-side on sm+ */}
      <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 sm:gap-8">
        {/* Score circle */}
        <div
          className="w-24 h-24 sm:w-28 sm:h-28 rounded-full flex flex-col items-center justify-center flex-shrink-0"
          style={{ border: `6px solid ${config.border}`, background: config.bg }}
          role="img"
          aria-label={`Honesty score: ${score} out of 5 — ${config.label}`}
        >
          <span className="text-3xl font-black text-[#1A2332]" style={{ fontFamily: "'Oswald', sans-serif" }} aria-hidden="true">
            {score}<span className="text-lg font-semibold text-[#6B7A8D]">/5</span>
          </span>
          <span className="text-[10px] font-bold uppercase tracking-widest mt-0.5" style={{ color: config.color }} aria-hidden="true">Paws</span>
        </div>

        {/* Text content — full width on mobile */}
        <div className="flex-1 min-w-0 w-full">
          <div className="text-lg sm:text-xl font-black mb-1 break-words" style={{ fontFamily: "'Oswald', sans-serif", color: config.color }}>
            {config.label}
          </div>
          <p className="text-[#6B7A8D] text-sm mb-3 leading-relaxed">{config.sub}</p>
          {/* Progress bar */}
          <div className="flex flex-col gap-1">
            <div
              className="w-full h-2.5 bg-[#E8E2D6] rounded-full overflow-hidden"
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${pct}% transparency`}
            >
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{ width: `${pct}%`, background: config.color }}
              />
            </div>
            <span className="text-xs font-bold text-[#6B7A8D]">{pct}% transparent</span>
          </div>
          <p className="text-[11px] text-[#9CA3AF] mt-2">
            <i className="ri-information-line mr-1" aria-hidden="true" />
            Based on vehicle history, service records, accident data &amp; our inspection
          </p>
        </div>
      </div>
    </section>
  );
});
CriticScore.displayName = 'CriticScore';

// ─── Showtimes ────────────────────────────────────────────────────────────────
const Showtimes = memo(({ vehicle, monthly, onAvailability, onPreApproval }: { vehicle: Vehicle; monthly: number; onAvailability: () => void; onPreApproval: () => void }) => {
  const isSold = vehicle.status === 'sold';
  const isPending = vehicle.status === 'pending';
  
  return (
    <section id="showtimes" className="rounded-2xl overflow-hidden shadow-xl border border-[#E8E2D6]" aria-labelledby="claim-heading">
      {/* Header band */}
      <div className="bg-[#1E1A14] px-5 sm:px-7 py-5 flex items-center gap-3">
        <i className="ri-car-line text-2xl" style={{ color: '#F5C842' }} aria-hidden="true" />
        <h2 id="claim-heading" className="text-xl sm:text-2xl font-black text-white" style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: '0.06em' }}>
          CLAIM THIS RIDE
        </h2>
      </div>

      {/* Body */}
      <div className="bg-[#FFFBF0] p-5 sm:p-7 lg:p-9">
        {/* Price row — stack on mobile */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-6 gap-3">
          <div>
            <div className="text-xs font-black text-[#6B7A8D] uppercase tracking-widest mb-1" id="asking-price-label">Asking Price</div>
            <div
              className="text-4xl sm:text-5xl font-black text-[#1A2332]"
              style={{ fontFamily: "'Oswald', sans-serif" }}
              aria-labelledby="asking-price-label"
            >
              ${vehicle.price.toLocaleString()}
            </div>
          </div>
          {monthly > 0 && (
            <div className="sm:text-right">
              <div className="text-xs font-black text-[#6B7A8D] uppercase tracking-widest mb-1" id="est-payment-label">Est. Payment</div>
              <div
                className="text-2xl sm:text-3xl font-black text-[#1A2332]"
                style={{ fontFamily: "'Oswald', sans-serif" }}
                aria-labelledby="est-payment-label"
              >
                ${Math.round(monthly).toLocaleString()}<span className="text-base sm:text-lg font-semibold">/mo</span>
              </div>
              <div className="text-xs text-[#6B7A8D] mt-0.5">OAC · 60 mo @ 5.9% APR · 10% down</div>
            </div>
          )}
        </div>

        {isSold ? (
          <div className="space-y-3">
            <div className="bg-red-50 border-2 border-red-400 rounded-xl p-4 text-center" role="status">
              <span className="text-lg sm:text-xl font-black text-red-700" style={{ fontFamily: "'Oswald', sans-serif" }}>🚫 SOLD — BUT WE CAN FIND YOU ONE</span>
            </div>
            <Link
              to="/inventory"
              className="w-full py-4 text-[#1E1A14] text-base font-black rounded-xl hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F5C842] focus-visible:ring-offset-2"
              style={{ background: '#F5C842', fontFamily: "'Oswald', sans-serif", letterSpacing: '0.06em' }}
            >
              <i className="ri-car-line" aria-hidden="true" />
              FIND ME SOMETHING SIMILAR
            </Link>
          </div>
        ) : isPending ? (
          <div className="space-y-3">
            <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-4 text-center" role="status">
              <span className="text-lg sm:text-xl font-black text-amber-700" style={{ fontFamily: "'Oswald', sans-serif" }}>⏳ UNDER CONTRACT — JOIN THE WAITLIST</span>
            </div>
            <button
              onClick={onAvailability}
              className="w-full py-4 text-[#1E1A14] text-base font-black rounded-xl hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F5C842] focus-visible:ring-offset-2"
              style={{ background: '#F5C842', fontFamily: "'Oswald', sans-serif", letterSpacing: '0.06em' }}
            >
              <i className="ri-notification-line" aria-hidden="true" />
              JOIN THE WAITLIST
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" role="group" aria-label="Purchase actions">
            <button
              onClick={onAvailability}
              className="py-4 text-white text-base font-black rounded-xl hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A2332]"
              style={{ background: '#1A2332', fontFamily: "'Oswald', sans-serif", letterSpacing: '0.06em' }}
            >
              CHECK AVAILABILITY
            </button>
            <Link
              to={`/apply-for-financing?vehicle=${encodeURIComponent(`${vehicle.year} ${vehicle.make} ${vehicle.model}`)}`}
              className="py-4 text-[#1E1A14] text-base font-black rounded-xl hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F5C842] focus-visible:ring-offset-2"
              style={{ background: '#F5C842', fontFamily: "'Oswald', sans-serif", letterSpacing: '0.06em' }}
            >
              GET APPROVED
            </Link>
          </div>
        )}

        <p className="text-center text-[#6B7A8D] text-sm flex items-center justify-center gap-2 mt-5">
          <i className="ri-map-pin-line" style={{ color: '#D97706' }} aria-hidden="true" />
          Available at Rockhound Auto · Warrenton, Oregon
        </p>
      </div>
    </section>
  );
});
Showtimes.displayName = 'Showtimes';

// ─── Main VDP ─────────────────────────────────────────────────────────────────
export default function VehicleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { vehicle, loading, error } = useVehicleById(id);
  const { aggregate, reviews: allReviews } = useReviews();
  const { addRecentlyViewed } = useRecentlyViewed();

  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showAvailability, setShowAvailability] = useState(false);
  const [showPreApproval, setShowPreApproval] = useState(false);

  const aggregateRating = aggregate.reviewCount > 0
    ? { ratingValue: aggregate.ratingValue, reviewCount: aggregate.reviewCount }
    : undefined;

  const approvedReviews = allReviews
    .filter(r => r.status === 'approved' && r.rating >= 4)
    .map(r => ({ name: r.name, rating: r.rating, text: r.text, created_at: r.created_at }));

  useVehicleDetailSchemas(vehicle ?? undefined, aggregateRating, approvedReviews);

  const videoUrl = vehicle?.video_url;
  const videoDuration = vehicle?.video_duration;
  const youtubeIdForSchema = videoUrl ? extractYouTubeId(videoUrl) : null;

  useVideoSchema(
    vehicle && youtubeIdForSchema
      ? {
          name: `${vehicle.year} ${vehicle.make} ${vehicle.model} — Video Walkthrough | Rockhound Auto`,
          description: `Watch a full video walkthrough of this ${vehicle.year} ${vehicle.make} ${vehicle.model}. ${vehicle.mileage.toLocaleString()} miles, ${vehicle.transmission}, ${vehicle.drivetrain}. Priced at $${vehicle.price.toLocaleString()}.`,
          thumbnailUrl: `https://img.youtube.com/vi/${youtubeIdForSchema}/maxresdefault.jpg`,
          uploadDate: vehicle.created_at ?? new Date().toISOString().split('T')[0],
          duration: videoDuration ?? undefined,
          contentUrl: `https://www.youtube.com/watch?v=${youtubeIdForSchema}`,
          embedUrl: `https://www.youtube.com/embed/${youtubeIdForSchema}`,
        }
      : null
  );

  const vehicleUrl = vehicle ? `https://rockhoundauto.com/inventory/${vehicle.uuid || vehicle.id}` : undefined;
  const vehicleImage = vehicle?.image || 'https://static.readdy.ai/image/b3efdc45e89a2660551e85a9fdfadec5/a59b3ad9d14210b4ce617fff8e7523fb.png';

  useSEOHead({
    title: vehicle
      ? `${vehicle.year} ${vehicle.make} ${vehicle.model} for Sale | $${vehicle.price.toLocaleString()} | Rockhound Auto`
      : 'Vehicle Details | Rockhound Auto',
    description: vehicle
      ? `${vehicle.year} ${vehicle.make} ${vehicle.model} for sale at Rockhound Auto. ${vehicle.mileage.toLocaleString()} miles, ${vehicle.transmission}, ${vehicle.drivetrain}. $${vehicle.price.toLocaleString()}. Serving Warrenton, Astoria & the Pacific Northwest.`
      : 'View vehicle details at Rockhound Auto in Warrenton, Oregon.',
    keywords: vehicle
      ? `${vehicle.year} ${vehicle.make} ${vehicle.model}, used ${vehicle.make} ${vehicle.model}, ${vehicle.bodyType || 'car'} for sale, Warrenton Oregon, Astoria, used cars`
      : undefined,
    canonicalUrl: vehicleUrl,
    ogImage: vehicleImage,
    ogImageAlt: vehicle ? `${vehicle.year} ${vehicle.make} ${vehicle.model}` : 'Vehicle for sale',
    ogType: 'product',
  });

  useEffect(() => { window.scrollTo(0, 0); }, [id]);

  useEffect(() => {
    if (!vehicle) return;
    addRecentlyViewed({
      id: vehicle.uuid || String(vehicle.id),
      year: vehicle.year,
      make: vehicle.make,
      model: vehicle.model,
      price: vehicle.price,
      image: vehicle.image,
      mileage: vehicle.mileage,
    });
  }, [vehicle, addRecentlyViewed]);

  const monthly = (() => {
    if (!vehicle || vehicle.price <= 0) return 0;
    const loan = Math.max(vehicle.price - Math.round(vehicle.price * 0.1), 0);
    const r = 5.9 / 100 / 12;
    const n = 60;
    if (r === 0) return loan / n;
    return (loan * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  })();

  if (loading) {
    return (
      <div className="pt-16 lg:pt-20">
        <VehicleDetailSkeleton />
      </div>
    );
  }

  if (!vehicle || error) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-6 py-20 text-center bg-[#F7F5F0]">
        <i className="ri-car-line text-6xl text-[#E8E2D6] mb-4" />
        <h2 className="text-2xl font-bold text-[#1A2332] mb-2">Vehicle Not Found</h2>
        <p className="text-[#6B7A8D] mb-6 max-w-sm">{error || 'This vehicle may have been sold or removed.'}</p>
        <Link to="/inventory" className="px-6 py-3 bg-[#F5C842] text-[#1E1A14] rounded-xl font-semibold hover:bg-[#1A2332] hover:text-white transition-colors whitespace-nowrap">
          Browse All Inventory
        </Link>
      </div>
    );
  }

  const images = vehicle.images && vehicle.images.length > 0 ? vehicle.images : [vehicle.image || ''];
  const vehicleName = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;

  return (
    <div className="bg-[#F7F5F0] min-h-screen">
      {/* Cinematic Hero */}
      <CinematicHero vehicle={vehicle} onWatchTrailer={() => setShowVideoModal(true)} />

      {/* Content — two-column on xl+ */}
      {/* pb-24 lg:pb-0 reserves space for the mobile sticky bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-36 lg:pb-12">
        <div className="flex gap-10 items-start">
          {/* Main content column */}
          <main className="flex-1 min-w-0 space-y-8" id="main-content" aria-label="Vehicle details">
            <Synopsis vehicle={vehicle} />
            <MoviePosterSpecs vehicle={vehicle} />
            <SceneGallery images={images} vehicleName={vehicleName} />
            <VideoWalkthroughSection vehicle={vehicle} onPlay={() => setShowVideoModal(true)} />
            <CriticScore honestyScore={vehicle.honesty_score} />
            <HowYourPurchaseHelps />
            <Showtimes
              vehicle={vehicle}
              monthly={monthly}
              onAvailability={() => setShowAvailability(true)}
              onPreApproval={() => setShowPreApproval(true)}
            />
          </main>

          {/* Sticky Sidebar — lg+ only */}
          <aside className="hidden lg:block" aria-label="Purchase options and quick links">
            <StickySidebar
              vehicle={vehicle}
              monthly={monthly}
              onAvailability={() => setShowAvailability(true)}
              onPreApproval={() => setShowPreApproval(true)}
            />
          </aside>
        </div>
      </div>

      {/* Similar Vehicles */}
      <section className="py-12 bg-[#FFFBF0] border-t border-[#E8E2D6]" aria-label="Similar vehicles">
        <ErrorBoundary fallback={null}>
          <Suspense fallback={<SectionFallback />}>
            <SimilarVehicles currentVehicle={vehicle} />
          </Suspense>
        </ErrorBoundary>
      </section>

      {/* Mobile Sticky Action Bar — only shows on lg and below */}
      <StickyActionBar
        vehicle={vehicle}
        onTestDrive={() => setShowAvailability(true)}
        onPreApproval={() => setShowPreApproval(true)}
      />

      {/* Video Modal */}
      {showVideoModal && vehicle.video_url && (
        <VideoModal
          videoUrl={vehicle.video_url}
          vehicleName={vehicleName}
          onClose={() => setShowVideoModal(false)}
        />
      )}

      {showAvailability && (
        <ErrorBoundary fallback={null}>
          <Suspense fallback={<SectionFallback />}>
            <CheckAvailabilityModal vehicle={vehicle} onClose={() => setShowAvailability(false)} />
          </Suspense>
        </ErrorBoundary>
      )}
      {showPreApproval && (
        <ErrorBoundary fallback={null}>
          <Suspense fallback={<SectionFallback />}>
            <PreApprovalForm vehicle={vehicle} onClose={() => setShowPreApproval(false)} />
          </Suspense>
        </ErrorBoundary>
      )}

      <BackToTop />
    </div>
  );
}
